import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ConnectionLost } from '../components/ConnectionLost.tsx';
import { Loading } from '../components/Loading.tsx';
import { FirstPasswordScreen } from '../screens/FirstPasswordScreen.tsx';
import { useAuth } from './AuthContext.tsx';

/**
 * Blank surface while the session check is in flight — no login flash.
 *
 * Blank for the first moment only. A session check on a working connection is
 * over before anyone could see a spinner; one on a phone with two bars can take
 * the full twenty seconds before the deadline, and a white app for twenty
 * seconds is indistinguishable from an app that failed to start. The longer
 * delay than elsewhere is because this is the very first frame of the app,
 * where a flicker costs more than it does mid-session.
 */
function Pending() {
  return (
    <div className="app-pending">
      <Loading delayMs={800} />
    </div>
  );
}

/**
 * The blank surface above, once it stops being a moment.
 *
 * Every one of these guards has the same two states and the same reason for
 * them: with an answer there is a screen to go to, and with no answer at all
 * there is no way to know which screen that would be. Drawing `Pending` for
 * that second case is what turned a bad mobile signal into an app that appears
 * to have failed to start — and it is checked before `loading`, so pressing
 * «Повторить» keeps this screen rather than blinking back to nothing.
 */
function Gate() {
  const { loading, unreachable, recheck } = useAuth();
  return unreachable ? <ConnectionLost busy={loading} onRetry={recheck} /> : <Pending />;
}

export function RequireAuth() {
  const { user, loading, unreachable } = useAuth();
  const location = useLocation();

  if (loading || unreachable) return <Gate />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  /**
   * A password somebody else handed out is replaced before anything else
   * happens. Drawn here rather than routed to, which is what makes it
   * inescapable: every authenticated URL is this screen while the flag is up,
   * and the chat layout below never mounts — so there is no socket, no chat
   * list, and nothing behind it to see. The server refuses the rest regardless;
   * this is what stops the app asking for it.
   */
  if (user.mustChangePassword) return <FirstPasswordScreen />;
  return <Outlet />;
}

/**
 * The admin flag is read from the users table on every request server-side, so
 * this is a convenience gate for the UI only — /api/admin enforces it for real.
 */
export function RequireAdmin() {
  const { user, loading, unreachable } = useAuth();

  if (loading || unreachable) return <Gate />;
  if (!user) return <Navigate to="/login" replace />;
  // Its own tree, so it has to say the same thing RequireAuth does — otherwise
  // an admin whose account was just created walks past the screen by typing a URL.
  if (user.mustChangePassword) return <FirstPasswordScreen />;
  if (!user.isAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Sends an already-logged-in visitor away from the login screen. */
export function RequireAnonymous() {
  const { user, loading, unreachable } = useAuth();

  // Here too, and it is not a formality: /login is where a phone that could not
  // reach the server ends up, and offering a password form to somebody who may
  // well still be signed in is how a bad signal turns into «меня разлогинило».
  if (loading || unreachable) return <Gate />;
  if (user) return <Navigate to="/" replace />;
  return <Outlet />;
}
